import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateWorksheet } from '../../api/worksheets';
import { fetchClasses } from '../../api/classes';
import { fetchTeacherProfile } from '../../api/teacherProfile';
import client from '../../api/client';
import StandardsPicker from '../LessonWizard/StandardsPicker';
import styles from './WorksheetWizardPage.module.css';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUBJECTS = ['Math', 'ELA', 'Science', 'Social Studies', 'Other'];
const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const WORKSHEET_TYPES = [
  { value: 'quiz', label: 'Quiz' },
  { value: 'homework', label: 'Homework' },
  { value: 'test', label: 'Test' },
  { value: 'worksheet', label: 'Worksheet' },
];
const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
  { value: 'mixed', label: 'Mixed' },
];
const QUESTION_TYPES = [
  { value: '', label: 'Mixed (Default)' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True/False' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'open_ended', label: 'Open-Ended Math' },
];
const MC_OPTIONS = [3, 4, 5];

export default function WorksheetWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode
  const [quickMode, setQuickMode] = useState(true);

  // Class-based mode
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [units, setUnits] = useState([]);

  // Quick mode fields
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');

  // Worksheet-specific fields
  const [worksheetType, setWorksheetType] = useState('quiz');
  const [difficulty, setDifficulty] = useState('medium');
  const [questionCount, setQuestionCount] = useState(10);
  const [numberOfOptions, setNumberOfOptions] = useState(4);
  const [questionType, setQuestionType] = useState('');
  const [freetext, setFreetext] = useState('');
  const [lessonTopic, setLessonTopic] = useState('');

  // Standards
  const [selectedStandards, setSelectedStandards] = useState([]);

  // Profile
  const [profileGrades, setProfileGrades] = useState([]);
  const [profileSubjects, setProfileSubjects] = useState([]);
  const [showAllGrades, setShowAllGrades] = useState(false);

  // UI
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from route state (from lesson editor)
  useEffect(() => {
    if (location.state?.lessonId) {
      if (location.state.subject) setSubject(location.state.subject);
      if (location.state.gradeLevel) setGradeLevel(location.state.gradeLevel);
      if (location.state.topic) setLessonTopic(location.state.topic);
    }
  }, [location.state]);

  // Load classes and profile on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [classResult, profile] = await Promise.allSettled([
          fetchClasses(),
          fetchTeacherProfile(),
        ]);
        if (classResult.status === 'fulfilled') {
          const data = classResult.value;
          setClasses(Array.isArray(data) ? data : data.data || data.classes || []);
        }
        if (profile.status === 'fulfilled' && profile.value) {
          const p = profile.value.profile || profile.value;
          setProfileGrades(p.gradesJson || []);
          setProfileSubjects(p.subjectsJson || []);
        }
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleClassSelect = async (cls) => {
    setSelectedClass(cls);
    setSelectedUnit(null);
    setUnits([]);
    setSelectedStandards([]);
    setLessonTopic('');
    setSubject(cls.subject || '');
    setGradeLevel(String(cls.gradeLevel || ''));
    try {
      const res = await client.get(`/api/classes/${cls.id}`, { headers: getAuthHeaders() });
      const classData = res.data.data || res.data;
      setUnits(classData.units || []);
    } catch {
      // Class may not have units
    }
  };

  // Auto-select grade when teacher only teaches one grade
  useEffect(() => {
    if (quickMode && profileGrades.length === 1 && !gradeLevel) {
      setGradeLevel(profileGrades[0]);
    }
  }, [profileGrades, quickMode, gradeLevel]);

  const enterQuickMode = () => {
    setQuickMode(true);
    setSelectedClass(null);
    setSelectedUnit(null);
    if (profileSubjects.length === 1) setSubject(profileSubjects[0]);
    if (profileGrades.length === 1) setGradeLevel(profileGrades[0]);
  };

  const exitQuickMode = () => {
    setQuickMode(false);
    setSubject('');
    setGradeLevel('');
    setSelectedStandards([]);
    setLessonTopic('');
    setShowAllGrades(false);
  };

  const handleAddStandard = (standard) => {
    setSelectedStandards(prev => [...prev, standard]);
  };

  const handleRemoveStandard = (standard) => {
    setSelectedStandards(prev => prev.filter(s => s.id !== standard.id));
  };

  const showMCOptions = questionType === 'multiple_choice' || questionType === '';

  const canGenerate = !!subject && !!gradeLevel && !!worksheetType && questionCount > 0;

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const typeLabel = worksheetType.charAt(0).toUpperCase() + worksheetType.slice(1);
      const result = await generateWorksheet({
        title: `${gradeLevel} ${subject} ${typeLabel}`,
        type: worksheetType,
        subject,
        gradeLevel,
        difficulty,
        questionCount: Number(questionCount) || 10,
        questionType: questionType || undefined,
        numberOfOptions: Number(numberOfOptions) || 4,
        standardCodes: selectedStandards.map(s => s.standardCode),
        topic: lessonTopic || undefined,
        freetext: freetext || undefined,
        classId: selectedClass?.id,
        unitId: selectedUnit?.id,
        lessonId: location.state?.lessonId,
      });
      navigate(`/worksheets/${result.data.id}/edit`);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to generate worksheet';
      setError(msg);
      setGenerating(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Create a Worksheet</h1>
        <p className={styles.subtitle}>
          {quickMode
            ? 'Configure your worksheet and generate it in one step.'
            : classes.length > 0
              ? 'Select a class or create a quick worksheet.'
              : 'Start a quick worksheet, or create a class first.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        {/* Quick Mode */}
        {quickMode && (
          <>
            {/* Subject */}
            <div className={styles.section}>
              <div className={styles.quickModeHeader}>
                <h2 className={styles.sectionTitle}>Subject</h2>
                <button className={styles.changeBtn} onClick={exitQuickMode}>
                  {classes.length > 0 ? 'Pick a class instead' : 'Back'}
                </button>
              </div>
              <div className={styles.unitList}>
                {SUBJECTS.map(s => (
                  <button
                    key={s}
                    className={`${styles.unitBtn} ${subject === s ? styles.unitBtnActive : ''}`}
                    onClick={() => setSubject(subject === s ? '' : s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Grade */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Grade</h2>
              <div className={styles.unitList}>
                {(profileGrades.length > 0 && !showAllGrades ? profileGrades : GRADES).map(g => (
                  <button
                    key={g}
                    className={`${styles.unitBtn} ${gradeLevel === g ? styles.unitBtnActive : ''}`}
                    onClick={() => setGradeLevel(gradeLevel === g ? '' : g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {profileGrades.length > 0 && (
                <button
                  className={styles.toggleLink}
                  onClick={() => setShowAllGrades(prev => !prev)}
                >
                  {showAllGrades ? 'Show my grades' : 'Show all grades'}
                </button>
              )}
            </div>

            {/* Worksheet Type */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Worksheet Type</h2>
              <div className={styles.buttonGroup}>
                {WORKSHEET_TYPES.map(t => (
                  <button
                    key={t.value}
                    className={`${styles.buttonGroupItem} ${worksheetType === t.value ? styles.buttonGroupItemActive : ''}`}
                    onClick={() => setWorksheetType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Type */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Question Type</h2>
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className={styles.select}
                aria-label="Question type"
              >
                {QUESTION_TYPES.map(qt => (
                  <option key={qt.value} value={qt.value}>{qt.label}</option>
                ))}
              </select>
            </div>

            {/* Question Count */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Number of Questions</h2>
              <input
                type="number"
                min={1}
                max={50}
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value) || 0)}
                className={styles.input}
                aria-label="Number of questions"
              />
            </div>

            {/* Difficulty */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Difficulty</h2>
              <div className={styles.buttonGroup}>
                {DIFFICULTY_OPTIONS.map(d => (
                  <button
                    key={d.value}
                    className={`${styles.buttonGroupItem} ${difficulty === d.value ? styles.buttonGroupItemActive : ''}`}
                    onClick={() => setDifficulty(d.value)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* MC Options — only when question type allows */}
            {showMCOptions && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Multiple Choice Options</h2>
                <div className={styles.buttonGroup}>
                  {MC_OPTIONS.map(n => (
                    <button
                      key={n}
                      className={`${styles.buttonGroupItem} ${numberOfOptions === n ? styles.buttonGroupItemActive : ''}`}
                      onClick={() => setNumberOfOptions(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Standards — when subject and grade selected */}
            {subject && gradeLevel && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Standards (optional)</h2>
                <p className={styles.sectionHint}>Select standards to align your worksheet.</p>
                <StandardsPicker
                  subject={subject}
                  gradeLevel={gradeLevel}
                  selectedStandards={selectedStandards}
                  onAdd={handleAddStandard}
                  onRemove={handleRemoveStandard}
                />
              </div>
            )}

            {/* Topic */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Topic / Focus (optional)</h2>
              <input
                type="text"
                value={lessonTopic}
                onChange={(e) => setLessonTopic(e.target.value)}
                placeholder="e.g., comparing fractions using visual models"
                className={styles.input}
                aria-label="Topic"
              />
            </div>

            {/* Specific Requests */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Specific Requests (optional)</h2>
              <textarea
                value={freetext}
                onChange={(e) => setFreetext(e.target.value)}
                placeholder="Any specific instructions or requests for the worksheet..."
                className={styles.textarea}
                rows={3}
              />
            </div>

            {/* Generate button */}
            <button
              className={styles.generateButton}
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
            >
              {generating ? 'Generating...' : 'Generate Worksheet'}
            </button>
          </>
        )}

        {/* Class selection mode */}
        {!quickMode && !selectedClass && (
          <>
            <button className={styles.quickBtn} onClick={enterQuickMode}>
              Quick Worksheet — skip class selection
            </button>

            {classes.length > 0 && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Or choose a class</h2>
                <div className={styles.cardGrid}>
                  {classes.map(cls => (
                    <button
                      key={cls.id}
                      className={styles.classCard}
                      onClick={() => handleClassSelect(cls)}
                    >
                      <span className={styles.classSubject}>{cls.subject}</span>
                      <span className={styles.classLabel}>{cls.name}</span>
                      <span className={styles.classGrade}>Grade {cls.gradeLevel}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Selected class mode */}
        {!quickMode && selectedClass && (
          <>
            <div className={styles.selectedCard}>
              <div>
                <span className={styles.classSubject}>{selectedClass.subject}</span>
                <span className={styles.classLabel}>{selectedClass.name}</span>
              </div>
              <button className={styles.changeBtn} onClick={() => { setSelectedClass(null); setSelectedUnit(null); setSelectedStandards([]); setLessonTopic(''); setSubject(''); setGradeLevel(''); }}>
                Change
              </button>
            </div>

            {/* Unit Selection */}
            {units.length > 0 && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Choose a Unit (optional)</h2>
                <div className={styles.unitList}>
                  {units.map(unit => (
                    <button
                      key={unit.id}
                      className={`${styles.unitBtn} ${selectedUnit?.id === unit.id ? styles.unitBtnActive : ''}`}
                      onClick={() => setSelectedUnit(selectedUnit?.id === unit.id ? null : unit)}
                    >
                      {unit.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Worksheet Type */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Worksheet Type</h2>
              <div className={styles.buttonGroup}>
                {WORKSHEET_TYPES.map(t => (
                  <button
                    key={t.value}
                    className={`${styles.buttonGroupItem} ${worksheetType === t.value ? styles.buttonGroupItemActive : ''}`}
                    onClick={() => setWorksheetType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Question Type */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Question Type</h2>
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className={styles.select}
                aria-label="Question type"
              >
                {QUESTION_TYPES.map(qt => (
                  <option key={qt.value} value={qt.value}>{qt.label}</option>
                ))}
              </select>
            </div>

            {/* Question Count */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Number of Questions</h2>
              <input
                type="number"
                min={1}
                max={50}
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value) || 0)}
                className={styles.input}
                aria-label="Number of questions"
              />
            </div>

            {/* Difficulty */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Difficulty</h2>
              <div className={styles.buttonGroup}>
                {DIFFICULTY_OPTIONS.map(d => (
                  <button
                    key={d.value}
                    className={`${styles.buttonGroupItem} ${difficulty === d.value ? styles.buttonGroupItemActive : ''}`}
                    onClick={() => setDifficulty(d.value)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* MC Options */}
            {showMCOptions && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Multiple Choice Options</h2>
                <div className={styles.buttonGroup}>
                  {MC_OPTIONS.map(n => (
                    <button
                      key={n}
                      className={`${styles.buttonGroupItem} ${numberOfOptions === n ? styles.buttonGroupItemActive : ''}`}
                      onClick={() => setNumberOfOptions(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Standards */}
            {subject && gradeLevel && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Standards (optional)</h2>
                <p className={styles.sectionHint}>Select standards to align your worksheet.</p>
                <StandardsPicker
                  subject={subject}
                  gradeLevel={gradeLevel}
                  selectedStandards={selectedStandards}
                  onAdd={handleAddStandard}
                  onRemove={handleRemoveStandard}
                />
              </div>
            )}

            {/* Topic */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Topic / Focus (optional)</h2>
              <input
                type="text"
                value={lessonTopic}
                onChange={(e) => setLessonTopic(e.target.value)}
                placeholder="e.g., comparing fractions using visual models"
                className={styles.input}
                aria-label="Topic"
              />
            </div>

            {/* Specific Requests */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Specific Requests (optional)</h2>
              <textarea
                value={freetext}
                onChange={(e) => setFreetext(e.target.value)}
                placeholder="Any specific instructions or requests for the worksheet..."
                className={styles.textarea}
                rows={3}
              />
            </div>

            {/* Generate */}
            <button
              className={styles.generateButton}
              onClick={handleGenerate}
              disabled={generating || !canGenerate}
            >
              {generating ? 'Generating...' : 'Generate Worksheet'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
